import React from 'react';
import { conf } from 'config';
import useRequest from 'hooks/useRequest';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Auth0Service } from 'services/Auth0Service';
import { ProfileService } from 'services/ProfileService';
import { LoggerService } from 'services/LoggerService';
import { useTranslation } from 'react-i18next';
import ProfileModel, { Auth0User } from 'store/types/Profile';

const logger = LoggerService.getLogger('i18nService');

export interface AuthContextType {
  user?: Auth0User;
  userError?: string;
  userLoading?: boolean;
  profile?: ProfileModel;
  profileError?: string;
  profileLoading?: boolean;
  refetchProfile: () => void;
  isLoggedIn: boolean;
  isStaff: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const AuthContextProvider: React.FunctionComponent = ({ children }) => {
  const { i18n } = useTranslation();
  const [isStaff, setIsStaff] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const { data: user, error: userError, loading: userLoading } = useRequest<Auth0User>(Auth0Service.getUser);
  const {
    data: profile,
    error: profileError,
    loading: profileLoading,
    refetch: refetchProfile,
  } = useRequest<ProfileModel>(user ? ProfileService.getCustomer : undefined);

  useEffect(() => {
    if (user) {
      setIsLoggedIn(true);

      const payload = user?.idTokenPayload;
      setIsStaff(payload ? payload[conf.netSuite.semiIsStaffKey] : false);
    } else {
      setIsLoggedIn(false);
      setIsStaff(false);
    }
  }, [user]);

  useEffect(() => {
    const language = profile?.customer?.language;
    if (language) {
      i18n
        .changeLanguage(language)
        .then(() => {
          logger.info('Set language to ' + language);
        })
        .catch((err) => {
          logger.error('Failed to update language ' + err);
        });
    }
  }, [i18n, profile]);

  const login = useCallback(() => {
    Auth0Service.login();
  }, []);

  const logout = useCallback(() => {
    Auth0Service.logout();
  }, []);

  const context = {
    user,
    userError,
    userLoading,
    profile,
    profileError,
    profileLoading,
    refetchProfile,
    isLoggedIn,
    isStaff,
    login,
    logout,
  };

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('AuthContext was used outside of its Provider');
  }

  return context;
};
